import React from "react";
import * as m from "../lib/model";

const DEBT_SOURCES = m.DEBT_SOURCE_LABELS;
const CLASS_OF = {
  "Sponsor / investor equity": "Common", "New LP equity": "Pref / Common", "Rolled legacy equity": "Common only", "Seller rollover": "Common only",
  "Add-on seller rollover": "Common only", "LP capital calls — add-on equity": "Called Yr 1+", "Retained cash flow — self-funded add-ons": "Reserve",
};

export const suBreakdown = (V) => {
  const su = m.sourcesUses(V);
  const consolidated = m.isRollupScope(V);
  const rateOf = {
    "Senior debt": `${V.rate}%`, "New senior facility": `${V.rate}%`, "Seller note": `${V.snRate}%`, "Seller / legacy note": `${V.snRate}%`,
    "Add-on senior debt": `${(V.addons && V.addons[0] && V.addons[0].rate) ?? V.rate}%`,
    "Add-on seller notes": `${(V.addons && V.addons[0] && V.addons[0].snRate) ?? V.snRate}%`,
  };
  const debt = su.sources.filter(([d]) => DEBT_SOURCES.has(d)).map(([d, a]) => ({
    d: d === "Add-on senior debt" && consolidated ? `Add-on senior debt (${V.addons.length} add-on${V.addons.length > 1 ? "s" : ""})` : d,
    a, int: rateOf[d],
  }));
  // One line per kind of equity: what LPs put in (close + every later call), what sellers roll, what the
  // business funds itself. Amounts are unchanged — only grouped so the table reads like a cap table.
  const raw = su.sources.filter(([d]) => !DEBT_SOURCES.has(d));
  const pick = (names) => raw.filter(([d]) => names.includes(d)).reduce((s, [, a]) => s + a, 0);
  const lpClose = pick(["Sponsor / investor equity", "New LP equity"]);
  const lpCalls = pick(["LP capital calls — add-on equity"]);
  const roll = pick(["Rolled legacy equity", "Seller rollover", "Add-on seller rollover"]);
  const selfCash = pick(["Retained cash flow — self-funded add-ons"]);
  const refi = raw.filter(([d]) => d.startsWith("Back-lever refinance proceeds")).reduce((s, [, a]) => s + a, 0);
  const known = new Set(["Sponsor / investor equity", "New LP equity", "LP capital calls — add-on equity", "Rolled legacy equity", "Seller rollover", "Add-on seller rollover", "Retained cash flow — self-funded add-ons", "SLB proceeds"]);
  const other = raw.filter(([d]) => !known.has(d) && !d.startsWith("Back-lever refinance proceeds")).map(([d, a]) => ({ d, a, t: CLASS_OF[d] || "Equity" }));
  const gp = m.exitReturns(V).gpShare || 0;
  // The leaseback is a property sale, never debt: the cash covers the real estate in Uses and
  // the surplus over what the property cost is equity the LPs never have to wire.
  const slbGross = pick(["SLB proceeds"]);
  const slbNet = slbGross - (Number(V.reVal) || 0);
  const equity = [
    { d: "Sale-leaseback proceeds — RE sold to the investor at close", a: slbGross, t: "No LP call", green: true,
      note: slbGross > 0.5 ? `${m.fmt$(Number(V.reVal) || 0)} covers the real estate in Uses · ${m.fmt$(slbNet)} net basis reduction funds equity` : undefined },
    { d: consolidated && lpCalls > 0.5 ? "LP equity — total commitment" : consolidated || m.isRecap(V) ? "LP equity (net raise)" : "Sponsor / investor equity", a: lpClose + lpCalls - gp,
      t: m.isRecap(V) || consolidated ? "Pref / Common" : "Common",
      note: lpCalls > 0.5 ? `${m.fmt$(lpClose - gp)} funded at close + ${m.fmt$(lpCalls)} called as the committed add-ons close` : undefined },
    { d: `GP co-invest — ${Number(V.gpCallPct) || 0}% of add-on equity`, a: gp, t: "Common", note: gp > 0.5 ? "the sponsor's own money alongside the LPs" : undefined },
    { d: "Seller rollover", a: roll, t: "Common only",
      note: roll > 0.5 ? `${m.fmtPct(m.exitReturns(V).rollOwnPct || 0)} of total equity — common only, behind the LP pref` : undefined },
    { d: "Self-funded cash (retained cash flow)", a: selfCash, t: "No LP call", note: selfCash > 0.5 ? "cash the business generated and reinvested — never raised" : undefined },
    { d: "Back-lever refinance proceeds reinvested", a: refi, t: "No LP call" },
    ...other,
  ].filter((e) => e.a > 0.5);
  return { su, debt, equity, uses: su.uses.map(([d, a]) => ({ d, a })), slb: slbGross,
    totalDebt: debt.reduce((s, x) => s + (x.a || 0), 0), totalEquity: equity.reduce((s, x) => s + (x.a || 0), 0) };
};

// Uses / Sources-Debt / Sources-Equity tables, shared by the Dashboard card and the Sources & Uses page.
export const SourcesUsesTables = ({ V, width = 680 }) => {
  const { su, debt, equity, uses, totalDebt, totalEquity } = suBreakdown(V);
  const off = Math.abs(su.gap) > 1;
  // One column grid for all three tables so % of total / Interest / Terms and Amount line up.
  const cols = <colgroup><col /><col style={{ width: 120 }} /><col style={{ width: 150 }} /></colgroup>;
  const tblStyle = { maxWidth: width, width: "100%", tableLayout: "fixed" };
  return (
    <>
      <table className="tbl su-aligned" style={tblStyle} data-testid="su-uses-table">
        {cols}
        <thead><tr><th>Uses</th><th className="num">% of total</th><th className="num">Amount</th></tr></thead>
        <tbody>
          {uses.map((u) => (
            <tr key={u.d}><td>{u.d}</td><td className="num">{su.totalUses ? ((u.a / su.totalUses) * 100).toFixed(1) : "0.0"}%</td><td className="num">{u.a ? m.fmt$(u.a) : "—"}</td></tr>
          ))}
          <tr className="total"><td>Total Uses</td><td className="num">100.0%</td><td className="num">{m.fmt$(su.totalUses)}</td></tr>
        </tbody>
      </table>

      <table className="tbl su-aligned" style={{ ...tblStyle, marginTop: 16 }} data-testid="su-debt-table">
        {cols}
        <thead><tr><th>Sources — Debt</th><th className="num">Interest</th><th className="num">Amount</th></tr></thead>
        <tbody>
          {debt.map((d) => (
            <tr key={d.d}><td className={d.green ? "pos" : ""}>{d.d}{d.note && <div style={{ fontSize: 10, color: "var(--text3)" }}>{d.note}</div>}</td><td className="num">{d.int || "—"}</td><td className={`num ${d.green ? "pos" : ""}`}>{d.a ? m.fmt$(d.a) : "—"}</td></tr>
          ))}
          {!debt.length && <tr><td colSpan={3} style={{ color: "var(--text3)" }}>All-equity — no debt in this structure</td></tr>}
          <tr className="total"><td>Total Debt</td><td className="num" /><td className="num">{m.fmt$(totalDebt)}</td></tr>
        </tbody>
      </table>

      {V.slbOn && (
        <div className="callout" data-testid="slb-callout" style={{ maxWidth: width }}>
          RE purchased for {m.fmt$(V.reVal)} → sold to investor for {m.fmt$(V.slbProceeds)} → net basis reduction of {m.fmt$(V.slbProceeds - V.reVal)} → effective acquisition cost {m.fmt$(m.nac(V))}
        </div>
      )}

      <table className="tbl su-aligned" style={{ ...tblStyle, marginTop: 16 }} data-testid="su-equity-table">
        {cols}
        <thead><tr><th>Sources — Equity</th><th className="num">Terms</th><th className="num">Amount</th></tr></thead>
        <tbody>
          {equity.map((e) => (
            <tr key={e.d}><td className={e.green ? "pos" : ""}>{e.d}{e.note && <div style={{ fontSize: 10, color: "var(--text3)", whiteSpace: "normal", lineHeight: 1.35 }}>{e.note}</div>}</td><td className="num" style={{ fontFamily: "var(--ui)", color: "var(--text2)" }}>{e.t}</td><td className={`num ${e.green ? "pos" : ""}`}>{e.a ? m.fmt$(e.a) : "—"}</td></tr>
          ))}
          <tr className="total"><td>Total Equity</td><td className="num" /><td className="num">{m.fmt$(totalEquity)}</td></tr>
          <tr className="hl"><td><b>Total Sources</b></td><td className="num" /><td className="num"><b>{m.fmt$(su.totalSources)}</b></td></tr>
          <tr data-testid="su-balance"><td style={{ color: off ? "var(--red)" : "var(--text3)" }}>{off ? "Out of balance vs Total Uses" : "Balanced against Total Uses"}</td><td className="num" /><td className="num" style={{ color: off ? "var(--red)" : "var(--text3)" }}>{off ? m.fmt$(su.gap) : m.fmt$(su.totalUses)}</td></tr>
        </tbody>
      </table>
    </>
  );
};
